import { useId } from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface BucketSelectProps {
    buckets: Array<{ id: number; name: string }>;
    value: number | undefined;
    onChange: (id: number | undefined) => void;
    size?: 'small' | 'medium';
    minWidth?: number;
    disabled?: boolean;
    // CALL-758: include an "All buckets" option (value = undefined). On the overview it shows every
    // version; on the config / prices pages it shows every bucket's rows.
    allowAll?: boolean;
    // Form use (allowAll={false}): stretch to the enclosing grid column and mark the field required,
    // instead of the fixed-width filter the page headers use.
    fullWidth?: boolean;
    required?: boolean;
}

// CALL-758: the bucket picker for the overview version filter and the Billing Configuration /
// Prices & ABT pages. It drives the "currently selected bucket" — undefined means "All buckets".
const BucketSelect = ({
    buckets,
    value,
    onChange,
    size = 'small',
    minWidth = 220,
    disabled,
    allowAll = true,
    fullWidth = false,
    required = false,
}: BucketSelectProps) => {
    const { t } = useTranslation('budget');
    // Ties the label to the Select. MUI does not wire this up on its own, so without it the field has
    // no accessible name at all — screen readers announce a bare combobox, and it is unreachable by
    // label in tests.
    const labelId = `${useId()}-bucket-label`;

    const handleChange = (event: SelectChangeEvent<number | ''>) => {
        const v = event.target.value;
        onChange(v === '' ? undefined : Number(v));
    };

    return (
        <FormControl
            size={size}
            sx={fullWidth ? undefined : { minWidth }}
            fullWidth={fullWidth}
            disabled={disabled}
            required={required}
        >
            {/* shrink + notched keep the label floating above the field even when the value is the
                empty "All buckets" option, so the label doesn't overlap the shown text. */}
            <InputLabel id={labelId} shrink>
                {t('field.bucket')}
            </InputLabel>
            <Select
                labelId={labelId}
                value={value ?? ''}
                onChange={handleChange}
                label={t('field.bucket')}
                notched
                displayEmpty={allowAll}
            >
                {allowAll && (
                    <MenuItem value="">
                        <em>{t('buckets.all')}</em>
                    </MenuItem>
                )}
                {buckets.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                        {b.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default BucketSelect;
